<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  // Fase 2, parte 6 (11/10/2026): o estado de uma integração configurada que
  // parou (Integrations::App#atencao). A frase curta vem da página da própria
  // integração (INTEGRACAO_ESCRITORIO.<ID>.ATENCAO), uma fonte só.
  atencao: {
    type: String,
    default: null,
  },
  // Cartão que não é integração da conta (Google mora no Perfil): ícones no
  // lugar do logo e o destino próprio.
  icones: {
    type: Array,
    default: () => [],
  },
  destino: {
    type: String,
    default: null,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const fraseDaAtencao = computed(() =>
  props.atencao
    ? t(
        `INTEGRACAO_ESCRITORIO.${String(props.id).toUpperCase()}.ATENCAO.${props.atencao}`
      )
    : ''
);

const actionURL = computed(() => {
  if (props.destino) return props.destino;
  if (props.id === 'menu_apps') {
    return frontendURL(
      `accounts/${accountId.value}/settings/integrations/menu-apps`
    );
  }
  return frontendURL(
    `accounts/${accountId.value}/settings/integrations/${props.id}`
  );
});
</script>

<template>
  <!-- O cartão inteiro abre a integração (padrão §1.8); antes só o link
       "Configurar" levava, e o resto do cartão não respondia ao clique. -->
  <router-link
    :to="actionURL"
    class="integ flex flex-col flex-1 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-xl"
    :class="{ 'integ--atencao': atencao }"
  >
    <div class="flex items-start justify-between">
      <div v-if="icones.length" class="integ__icones mb-2" aria-hidden="true">
        <i v-for="icone in icones" :key="icone" :class="icone" />
      </div>
      <div v-else class="flex h-12 w-12 mb-2">
        <img
          :src="`/dashboard/images/integrations/${id}.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${id}-dark.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>
      <!-- Selo só na exceção: "Desativado" em seis cartões de sete era
           marcar todos (padrão §1.2). Ligada é o que merece ser visto. -->
      <Label
        v-if="atencao"
        :label="t('INTEGRATION_APPS.STATUS.ATENCAO')"
        color="amber"
        compact
      />
      <Label
        v-else-if="enabled"
        :label="t('INTEGRATION_APPS.STATUS.ENABLED')"
        color="teal"
        compact
      />
    </div>
    <div class="flex flex-col m-0 flex-1">
      <div
        class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
      >
        <span class="text-heading-3 text-n-slate-12">{{ name }}</span>
        <span class="integ__acao">
          <i class="i-ph-gear-six" aria-hidden="true" />
          {{ $t('INTEGRATION_APPS.CONFIGURE') }}
        </span>
      </div>
      <p class="text-n-slate-11 text-body-main">
        {{ replaceInstallationName(description) }}
      </p>
      <p v-if="atencao" class="integ__atencao">
        <i class="i-ph-warning-circle-fill" aria-hidden="true" />
        {{ fraseDaAtencao }}
      </p>
    </div>
  </router-link>
</template>

<style scoped>
.integ {
  transition:
    transform var(--mov-2) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva);
}

.integ--atencao {
  outline-color: rgb(var(--amber-7));
}

.integ__icones {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  height: 48px;
}

.integ__icones > i {
  width: 32px;
  height: 32px;
  color: rgb(var(--slate-11));
}

.integ__atencao {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: var(--esp-3) 0 0;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--amber-11));
}

.integ__atencao > i {
  flex-shrink: 0;
  width: var(--ico-2);
  height: var(--ico-2);
}

.integ:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px -10px rgb(var(--slate-12) / 0.25);
}

.integ__acao {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-10));
  transition: color var(--mov-2) var(--mov-curva);
}

.integ:hover .integ__acao {
  color: rgb(var(--blue-11));
}

@media (prefers-reduced-motion: reduce) {
  .integ,
  .integ:hover {
    transition: none;
    transform: none;
  }
}
</style>
